import type { Object3D } from 'three';
import type { CollectSource, ISketchObject } from '../../../mesh/sketch/ISketchObject';
import type { SketchGroup } from '../../../mesh/sketch/SketchGroup';
import type {SketchCollectionObject} from '../../../mesh/sketch/SketchCollectionObject';
import {
	isSketchArcObject,
	isSketchCollectionObject,
	isSketchLineObject,
	isSketchParabolaObject,
	isSketchSplineObject,
	isSketchStyleSplineObject,
	isSketchObject,
} from '../../../mesh/guards';
import { resolvePreferredSourceDisplayLabel } from '../model/EdgeLabelUtils';
import { clearSketchEntityReferenceTag, writeSketchEntityReferenceTag } from '../model/SketchEntityReferenceIds';
import {
	buildSketchEntityReferenceRecord,
	resolveSourceRefEntityId,
	restoreProjectionResultFromReferenceRecord,
	type SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import type { ProjectionResult } from '../model/ProjectionResult';
import {resolveExternalGeometrySourceDescriptorKey} from '../model/ExternalGeometrySourceDescriptor';
import type { TargetAnchorBindings } from '../model/TargetAnchorBindings';
import {addObject3DToParent} from '../../../utils/scene-object/addObject3DToParent';
import { createExecutionDiagnostic, type ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { SketchReferenceEntityFactory } from '../target/SketchReferenceEntityFactory';
import type { SketchEntityReferenceRepositoryWriteScope } from '../repository/SketchEntityReferenceRepository';
import { SketchEntityReferenceObjectCleanupService } from '../target/SketchEntityReferenceObjectCleanupService';
import type {SketchEntityReferenceTargetRetirement} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceObjectCleanupService';
import { SketchReferenceRelationAttachDispatcher } from '../relation/SketchReferenceRelationAttachDispatcher';
import type { SketchEntityReferenceProjectionPort } from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceProjectionService';
import {
	SketchEntityReferenceTargetSnapshotService,
	type SketchEntityReferenceTargetSnapshot,
} from './SketchEntityReferenceTargetSnapshotService';
import { SketchEntityReferenceProjectionMutationDispatcher } from './SketchEntityReferenceProjectionMutationDispatcher';
import type {
	RebuildReferenceRuntimeItem,
	RebuildSingleReferenceResult,
	SketchEntityReferenceCollectionReplacementSnapshot,
	SketchEntityReferenceRebuildExecutorDependencies,
	SketchEntityReferenceRebuildExecutorInput,
	SketchEntityReferenceRebuildExecutorPort,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildExecutor';
import {STALE_SKETCH_ENTITY_REFERENCE_REBUILD} from './SketchEntityReferenceRebuildBatchTransaction';
import {executeSketchEntityReferenceRepositoryWrite} from '../utils/executeSketchEntityReferenceRepositoryWrite';

/** 保留执行器内部的短名称，但统一使用 checkpoint 共享 stale 信号。 */
const STALE_REFERENCE_REBUILD = STALE_SKETCH_ENTITY_REFERENCE_REBUILD;

/**
 * 单条转换实体引用重建执行器。
 *
 * 说明：
 * 1. 对一条记录完成来源解析、重新投影、目标修改以及失败回滚；
 * 2. 批次查询、基础约束恢复和传播提交不属于本类；
 * 3. same-kind 与 kind-changed 的完整事务语义封装在这一接口之后。
 */
export class SketchEntityReferenceRebuildExecutor implements SketchEntityReferenceRebuildExecutorPort {
	private readonly entityFactory: SketchReferenceEntityFactory;
	private readonly relationAttachDispatcher: SketchReferenceRelationAttachDispatcher;
	private readonly objectCleanupService: SketchEntityReferenceObjectCleanupService;
	private readonly targetSnapshotService: SketchEntityReferenceTargetSnapshotService;
	private readonly projectionMutationDispatcher: SketchEntityReferenceProjectionMutationDispatcher;
	private readonly projectionService: SketchEntityReferenceProjectionPort;

	/**
	 * @param dependencies 单条引用重建依赖。
	 */
	public constructor(private readonly dependencies: SketchEntityReferenceRebuildExecutorDependencies) {
		this.entityFactory =
			dependencies.entityFactory ??
			new SketchReferenceEntityFactory({
				lineReferenceBuilder: dependencies.lineReferenceBuilder,
				circleReferenceBuilder: dependencies.circleReferenceBuilder,
				arcReferenceBuilder: dependencies.arcReferenceBuilder,
				ellipseReferenceBuilder: dependencies.ellipseReferenceBuilder,
				ellipseArcReferenceBuilder: dependencies.ellipseArcReferenceBuilder,
				parabolaReferenceBuilder: dependencies.parabolaReferenceBuilder,
				splineReferenceBuilder: dependencies.splineReferenceBuilder,
				styleSplineReferenceBuilder: dependencies.styleSplineReferenceBuilder,
				objectCleanupService: dependencies.objectCleanupService,
			});
		this.relationAttachDispatcher = dependencies.relationAttachDispatcher ?? new SketchReferenceRelationAttachDispatcher(dependencies);
		this.objectCleanupService = dependencies.objectCleanupService ?? new SketchEntityReferenceObjectCleanupService();
		this.targetSnapshotService = dependencies.targetSnapshotService ?? new SketchEntityReferenceTargetSnapshotService();
		this.projectionMutationDispatcher = dependencies.projectionMutationDispatcher ?? new SketchEntityReferenceProjectionMutationDispatcher();
		this.projectionService = dependencies.projectionService;
	}

	/**
	 * 解析草图来源的当前运行时名称。
	 *
	 * @param reference 引用记录。
	 * @returns 当前名称；非草图来源或对象缺失时返回 null。
	 */
	private resolveRuntimeSourceName(reference: SketchEntityReferenceRecord): string | null {
		if (reference.sourceDescriptor.kind !== 'sketch-entity') {
			return null;
		}
		return this.dependencies.runtime.getSketchObject(reference.sourceDescriptor.sourceSketchId, reference.sourceDescriptor.entityId)?.name ?? null;
	}

	/**
	 * 解析引用记录的稳定来源显示标签。
	 *
	 * @param reference 引用记录。
	 * @param runtimeSourceName 当前运行时名称。
	 * @returns 规范化来源标签。
	 */
	private resolveReferenceSourceDisplayLabel(reference: SketchEntityReferenceRecord, runtimeSourceName: string | null): string {
		return resolvePreferredSourceDisplayLabel({
			runtimeSourceName,
			persistedSourceDisplayLabel: reference.sourceDisplayLabel,
		});
	}

	/**
	 * 重建单条引用记录。
	 *
	 * @param reference 引用记录。
	 * @returns 单条重建结果。
	 */
	public async execute(input: SketchEntityReferenceRebuildExecutorInput): Promise<RebuildSingleReferenceResult> {
		const { reference } = input;
		const repository = input.repository ?? this.dependencies.repository;
		const isInputCurrent = input.isInputCurrent ?? (() => true);
		if (!isInputCurrent()) {
			return {status: 'kept', diagnostics: []};
		}
		const targetSketch = this.dependencies.runtime.getSketchGroup(reference.targetSketchId);
		if (!targetSketch) {
			return this.cleanupDanglingReference({
				reference,
				repository,
				isInputCurrent,
				diagnostic: createExecutionDiagnostic({
					kind: 'target-missing-cleanup',
					level: 'warning',
					message: `目标草图已不存在，自动移除引用记录: ${reference.targetSketchId}`,
					referenceId: reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
					targetEntityId: reference.targetEntityId,
				}),
			});
		}
		if (this.dependencies.sourceObjectResolver.shouldSkip(reference.sourceDescriptor)) {
			const targetEntity = this.dependencies.runtime.getSketchObject(reference.targetSketchId, reference.targetEntityId);
			if (!targetEntity) {
				return this.cleanupDanglingReference({
					reference,
					repository,
					isInputCurrent,
					diagnostic: createExecutionDiagnostic({
						kind: 'target-missing-cleanup',
						level: 'warning',
						message: `目标实体已不存在，自动移除 topology-edge 引用记录: ${reference.targetEntityId}`,
						referenceId: reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
						targetEntityId: reference.targetEntityId,
					}),
				});
			}

			return {
				status: 'kept',
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'unsupported-source',
						level: 'warning',
						message: `topology-edge 来源暂不参与普通重建刷新，保留既有转换实体引用: ${reference.referenceId}`,
						referenceId: reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
						targetEntityId: reference.targetEntityId,
					}),
				],
			};
		}
		const targetSketchBaselineDefinitionRevision =
			input.targetSketchBaselineDefinitionRevisions.get(reference.targetSketchId) ?? targetSketch.definitionRevision;
		if (
			input.dirtyTargetSketchIdsAtBatchStart.has(reference.targetSketchId) ||
			targetSketch.hasDefinitionChangesOutsideOwnerSince(targetSketchBaselineDefinitionRevision, input.definitionChangeOwnerId)
		) {
			if (!isInputCurrent()) {
				return {status: 'kept', diagnostics: []};
			}
			await repository.updateReferenceStatus(reference.referenceId, 'failed');
			return {
				status: 'failed',
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'rebuild-failed',
						level: 'error',
						message: `目标草图存在未提交定义变更，跳过引用重建以避免吞并事务: ${reference.targetSketchId}`,
						referenceId: reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
						targetEntityId: reference.targetEntityId,
					}),
				],
			};
		}

		const runtimeSourceObject = this.dependencies.sourceObjectResolver.resolve(reference.sourceDescriptor);
		// topology-edge 的安全失败必须由 source resolver 诊断并保留目标，不能在前置对象解析阶段误删。
		if (!runtimeSourceObject && reference.sourceDescriptor.kind !== 'topology-edge') {
			if (input.sourceMissingPolicy === 'dangling') {
				return this.markSourceDangling(reference, [], isInputCurrent, repository);
			}
			// 来源缺失只代表依赖暂时悬空；删除 Record 或目标会破坏 Undo 恢复和警告定位。
			// dangling 状态由明确的生命周期删除事件负责，本重建路径只报告并保留现状。
			return {
				status: 'kept',
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'source-missing',
						level: 'warning',
						message: `来源实体当前不存在，保留转换实体引用等待恢复: ${resolveSourceRefEntityId(reference.sourceDescriptor)}`,
						referenceId: reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
						targetEntityId: reference.targetEntityId,
					}),
				],
			};
		}

		const projectionResultResult = this.projectionService.project({
			targetSketch,
			sourceDescriptors: [input.topologySource?.descriptor ?? reference.sourceDescriptor],
			...(input.topologySource ? {runtimeTopologySourcesBySourceKey: {
				[resolveExternalGeometrySourceDescriptorKey(input.topologySource.descriptor)]: input.topologySource.runtimeSource,
			}} : {}),
		});
		const diagnostics = [...projectionResultResult.diagnostics];
		if (
			input.sourceMissingPolicy === 'dangling'
			&& projectionResultResult.projectionResults.length <= 0
			&& diagnostics.some((diagnostic) => (
				diagnostic.kind === 'source-missing'
					|| diagnostic.kind === 'source-ambiguous'
					|| diagnostic.kind === 'unsafe-rebind'
			))
		) {
			return this.markSourceDangling(reference, diagnostics, isInputCurrent, repository);
		}
		if (projectionResultResult.diagnostics.some(diagnostic => diagnostic.level === 'error') || projectionResultResult.projectionResults.length <= 0) {
			if (!isInputCurrent()) {
				return {status: 'kept', diagnostics: []};
			}
			await repository.updateReferenceStatus(reference.referenceId, 'failed');
			diagnostics.push(
				createExecutionDiagnostic({
					kind: 'rebuild-failed',
					level: 'error',
					message: `重建引用失败：无法为引用重新生成有效投影 ${reference.referenceId}`,
					referenceId: reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
					targetEntityId: reference.targetEntityId,
				})
			);
			return {
				status: 'failed',
				diagnostics,
			};
		}

		const nextProjectionResult = projectionResultResult.projectionResults[0];
		if (!nextProjectionResult) {
			if (!isInputCurrent()) {
				return {status: 'kept', diagnostics: []};
			}
			await repository.updateReferenceStatus(reference.referenceId, 'failed');
			return {
				status: 'failed',
				diagnostics: [
					...diagnostics,
					createExecutionDiagnostic({
						kind: 'rebuild-failed',
						level: 'error',
						message: `重建引用失败：未取得新的投影结果 ${reference.referenceId}`,
						referenceId: reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
						targetEntityId: reference.targetEntityId,
					}),
				],
			};
		}

		const targetEntity = this.dependencies.runtime.getSketchObject(reference.targetSketchId, reference.targetEntityId);
		if (!targetEntity) {
			return this.cleanupDanglingReference({
				reference,
				repository,
				isInputCurrent,
				diagnostic: createExecutionDiagnostic({
					kind: 'target-missing-cleanup',
					level: 'warning',
					message: `目标实体已不存在，自动移除引用记录: ${reference.targetEntityId}`,
					referenceId: reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
					targetEntityId: reference.targetEntityId,
				}),
			});
		}
		if (!isSketchObject(targetEntity)) {
			if (!isInputCurrent()) {
				return {status: 'kept', diagnostics: []};
			}
			await repository.updateReferenceStatus(reference.referenceId, 'failed');
			return {
				status: 'failed',
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'rebuild-failed',
						level: 'error',
						message: `目标实体不是正式草图对象，无法执行引用重建: ${reference.targetEntityId}`,
						referenceId: reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
						targetEntityId: reference.targetEntityId,
					}),
				],
			};
		}

		const runtimeSourceName = this.resolveRuntimeSourceName(reference);
		const sourceDisplayLabel = this.resolveReferenceSourceDisplayLabel(reference, runtimeSourceName);

		if (
			!input.forceTargetReplacement &&
			nextProjectionResult.projectionResultKind === reference.projectionResultKind &&
			this.projectionMutationDispatcher.canMutateInPlace(targetEntity, nextProjectionResult)
		) {
			return this.rebuildSameKindReference({
				reference,
				targetEntity,
				nextProjectionResult,
				sourceDisplayLabel,
				runtimeSourceName,
				targetSketch,
				definitionChangeOwnerId: input.definitionChangeOwnerId,
				targetSketchBaselineDefinitionRevision,
				diagnostics,
				isInputCurrent,
				repository,
				transaction: input.transaction,
				batchKey: input.batchKey ?? reference.referenceId,
			});
		}

		return this.rebuildKindChangedReference({
			reference,
			targetSketch,
			targetEntity,
			nextProjectionResult,
			sourceDisplayLabel,
			runtimeSourceName,
			definitionChangeOwnerId: input.definitionChangeOwnerId,
			targetSketchBaselineDefinitionRevision,
			diagnostics,
			isInputCurrent,
			repository,
			transaction: input.transaction,
			batchKey: input.batchKey ?? reference.referenceId,
			...(input.preferredTargetAnchorBindings
				? {preferredTargetAnchorBindings: input.preferredTargetAnchorBindings}
				: {}),
		});
	}

	/**
	 * 在阶段 B checkpoint 中把无法重定位的来源写为 dangling。
	 *
	 * 目标草图实体和引用元数据全部保留；dangling 只形成 warning，不提升为本轮
	 * Feature error。后续来源恢复时，既有重建入口仍可把该记录转回 active。
	 *
	 * @param reference 当前引用记录。
	 * @param diagnostics 来源解析已经产生的诊断。
	 * @param isInputCurrent 当前异步任务是否仍属于活动文档。
	 * @returns 保留目标的单条重建结果。
	 */
	private async markSourceDangling(
		reference: SketchEntityReferenceRecord,
		diagnostics: ExecutionDiagnostic[] = [],
		isInputCurrent: () => boolean = () => true,
		repository: SketchEntityReferenceRepositoryWriteScope = this.dependencies.repository,
	): Promise<RebuildSingleReferenceResult> {
		if (!isInputCurrent()) {
			return {status: 'kept', diagnostics: []};
		}
		const fallbackDiagnostic = createExecutionDiagnostic({
			kind: 'source-missing',
			level: 'warning',
			message: `历史 checkpoint 中无法重定位来源，保留正式投影并标记为 dangling: ${reference.referenceId}`,
			referenceId: reference.referenceId,
			sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
			targetSketchId: reference.targetSketchId,
			targetEntityId: reference.targetEntityId,
		});
		const sourceDiagnostic = diagnostics.find((diagnostic) => (
			diagnostic.kind === 'source-missing'
			|| diagnostic.kind === 'source-ambiguous'
			|| diagnostic.kind === 'unsafe-rebind'
		)) ?? fallbackDiagnostic;
		const persistedDiagnostic = createExecutionDiagnostic({
			...sourceDiagnostic,
			referenceId: reference.referenceId,
			sourceRefEntityId: sourceDiagnostic.sourceRefEntityId
				?? resolveSourceRefEntityId(reference.sourceDescriptor),
			targetSketchId: reference.targetSketchId,
			targetEntityId: reference.targetEntityId,
		});
		try {
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				repository,
				isInputCurrent,
				STALE_REFERENCE_REBUILD,
				async tx => {
					if (!isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
					await tx.updateReferenceStatus(
						reference.referenceId,
						'dangling',
						persistedDiagnostic,
					);
					if (!isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
				},
			);
		} catch (error) {
			if (error === STALE_REFERENCE_REBUILD || !isInputCurrent()) {
				return {status: 'kept', diagnostics: []};
			}
			throw error;
		}
		return {
			status: 'kept',
			diagnostics: [
				...diagnostics.filter((diagnostic) => diagnostic !== sourceDiagnostic),
				persistedDiagnostic,
			],
		};
	}

	/**
	 * 执行“投影语义未变”的原位重建。
	 *
	 * @param input 原位重建输入。
	 * @returns 重建结果。
	 */
	private async rebuildSameKindReference(input: {
		reference: SketchEntityReferenceRecord;
		targetSketch: SketchGroup;
		targetEntity: Object3D;
		nextProjectionResult: ProjectionResult;
		sourceDisplayLabel: string;
		runtimeSourceName: string | null;
		definitionChangeOwnerId: string;
		targetSketchBaselineDefinitionRevision: number;
		diagnostics: ExecutionDiagnostic[];
		isInputCurrent: () => boolean;
		repository: SketchEntityReferenceRepositoryWriteScope;
		transaction?: NonNullable<SketchEntityReferenceRebuildExecutorInput['transaction']>;
		batchKey: string;
	}): Promise<{
		status: 'kept' | 'failed';
		diagnostics: ExecutionDiagnostic[];
		runtimeItem?: RebuildReferenceRuntimeItem;
	}> {
		if (!input.isInputCurrent()) {
			return {status: 'kept', diagnostics: []};
		}
		const targetSnapshot = this.targetSnapshotService.capture(input.targetEntity);
		input.transaction?.recordBeforeImageRestoration({
			targetSketchId: input.reference.targetSketchId,
			batchKey: input.batchKey,
			description: `恢复原位重建 before-image ${input.reference.referenceId}`,
			restoreRepository: () => input.repository.saveReferences([input.reference]),
			restoreRuntime: () => {
				this.rollbackStaleInPlaceRebuild({
					reference: input.reference,
					targetSketch: input.targetSketch,
					targetEntity: input.targetEntity,
					targetSnapshot,
					runtimeSourceName: input.runtimeSourceName,
					definitionChangeOwnerId: input.definitionChangeOwnerId,
				});
			},
		});
		try {
			const projectionState = input.targetSketch.runDefinitionChangeOwnerScope(input.definitionChangeOwnerId, () => {
				this.relationAttachDispatcher.detach({
					reference: input.reference,
					targetEntity: input.targetEntity,
				});
				return this.projectionMutationDispatcher.mutateInPlace(input.reference, input.targetEntity, input.nextProjectionResult);
			});
			const updatedReference = buildSketchEntityReferenceRecord({
				referenceId: input.reference.referenceId,
				projectionResult: input.nextProjectionResult,
				targetSketchId: input.reference.targetSketchId,
				targetEntityId: input.reference.targetEntityId,
				sourceDisplayLabel: input.sourceDisplayLabel,
				selectionIntent: input.reference.selectionIntent,
				boundaryMemberState: input.reference.boundaryMemberState,
				projectionState,
			});
			const attachResult = input.targetSketch.runDefinitionChangeOwnerScope(input.definitionChangeOwnerId, () =>
				this.relationAttachDispatcher.attach({
					reference: updatedReference,
					projectionResult: input.nextProjectionResult,
					targetEntity: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName ?? undefined,
				})
			);
			input.diagnostics.push(...attachResult.diagnostics);
			if (attachResult.diagnostics.some(diagnostic => diagnostic.level === 'error') || !attachResult.record) {
				if (input.transaction) {
					input.diagnostics.push(...await input.transaction.rollbackBatch(input.batchKey));
				} else {
					this.rollbackInPlaceRebuild({
						reference: input.reference,
						targetEntity: input.targetEntity,
						targetSnapshot,
					});
				}
				await input.repository.updateReferenceStatus(input.reference.referenceId, 'failed');
				input.diagnostics.push(
					createExecutionDiagnostic({
						kind: 'rebuild-failed',
						level: 'error',
						message: `原位重建后重新挂载引用约束失败: ${input.reference.referenceId}`,
						referenceId: input.reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
						targetEntityId: input.reference.targetEntityId,
					})
				);
				return {
					status: 'failed',
					diagnostics: input.diagnostics,
				};
			}

			const savedReference: SketchEntityReferenceRecord = {
				...updatedReference,
				projectionState: attachResult.record.projectionState ?? updatedReference.projectionState,
			};

			this.writeReferenceTag(input.targetEntity, savedReference.referenceId);
			if (!input.isInputCurrent()) {
				throw STALE_REFERENCE_REBUILD;
			}
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				input.repository,
				input.isInputCurrent,
				STALE_REFERENCE_REBUILD,
				async tx => {
					if (!input.isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
					await tx.saveReferences([savedReference]);
					if (!input.isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
					await tx.updateReferenceStatus(savedReference.referenceId, 'active');
					if (!input.isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
				},
			);
			if (!input.isInputCurrent()) {
				throw STALE_REFERENCE_REBUILD;
			}
			return {
				status: 'kept',
				diagnostics: input.diagnostics,
				runtimeItem: {
					referenceId: savedReference.referenceId,
					projectionResult: input.nextProjectionResult,
					targetEntity: input.targetEntity,
					targetChange: {
						referenceId: savedReference.referenceId,
						targetSketchId: savedReference.targetSketchId,
						oldTargetEntityId: input.reference.targetEntityId,
						newTargetEntityId: savedReference.targetEntityId,
						changeKind: 'in-place',
					},
					targetSketchBaselineDefinitionRevision: input.targetSketchBaselineDefinitionRevision,
				},
			};
		} catch (error) {
			if (error === STALE_REFERENCE_REBUILD || !input.isInputCurrent()) {
				if (input.transaction) {
					throw STALE_REFERENCE_REBUILD;
				}
				this.rollbackStaleInPlaceRebuild({
					reference: input.reference,
					targetSketch: input.targetSketch,
					targetEntity: input.targetEntity,
					targetSnapshot,
					runtimeSourceName: input.runtimeSourceName,
					definitionChangeOwnerId: input.definitionChangeOwnerId,
				});
				return {status: 'kept', diagnostics: []};
			}
			if (input.transaction) {
				input.diagnostics.push(...await input.transaction.rollbackBatch(input.batchKey));
			} else {
				this.rollbackInPlaceRebuild({
					reference: input.reference,
					targetEntity: input.targetEntity,
					targetSnapshot,
				});
			}
			await input.repository.updateReferenceStatus(input.reference.referenceId, 'failed');
			return {
				status: 'failed',
				diagnostics: [
					...input.diagnostics,
					createExecutionDiagnostic({
						kind: 'rebuild-failed',
						level: 'error',
						message: `原位重建引用失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: input.reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
						targetEntityId: input.reference.targetEntityId,
					}),
				],
			};
		}
	}

	/**
	 * 执行“投影语义变化”的替换重建。
	 *
	 * @param input 替换重建输入。
	 * @returns 重建结果。
	 */
	private async rebuildKindChangedReference(input: {
		reference: SketchEntityReferenceRecord;
		targetSketch: SketchGroup;
		targetEntity: Object3D & ISketchObject;
		nextProjectionResult: ProjectionResult;
		sourceDisplayLabel: string;
		runtimeSourceName: string | null;
		definitionChangeOwnerId: string;
		targetSketchBaselineDefinitionRevision: number;
		diagnostics: ExecutionDiagnostic[];
		isInputCurrent: () => boolean;
		repository: SketchEntityReferenceRepositoryWriteScope;
		transaction?: NonNullable<SketchEntityReferenceRebuildExecutorInput['transaction']>;
		batchKey: string;
		preferredTargetAnchorBindings?: TargetAnchorBindings;
	}): Promise<{
		status: 'kept' | 'failed';
		diagnostics: ExecutionDiagnostic[];
		runtimeItem?: RebuildReferenceRuntimeItem;
	}> {
		if (!input.isInputCurrent()) {
			return {status: 'kept', diagnostics: []};
		}
		let createdTargetEntity: (Object3D & ISketchObject) | null = null;
		let collectionReplacementSnapshot: SketchEntityReferenceCollectionReplacementSnapshot | null = null;
		let targetRetirement: SketchEntityReferenceTargetRetirement | null = null;
		let transientTargetPersistentId: string | null = null;
		let replacementIdentityApplied = false;
		try {
			const factoryResult = input.targetSketch.runDefinitionChangeOwnerScope(input.definitionChangeOwnerId, () =>
				this.entityFactory.createBatch({
					targetSketch: input.targetSketch,
					projectionResults: [input.nextProjectionResult],
					...(input.preferredTargetAnchorBindings
						? {preferredTargetAnchorBindings: input.preferredTargetAnchorBindings}
						: {}),
					resolveSourceObject: descriptor => this.dependencies.sourceObjectResolver.resolve(descriptor),
				})
			);
			input.diagnostics.push(...factoryResult.diagnostics);
			if (factoryResult.diagnostics.some(diagnostic => diagnostic.level === 'error')) {
				await input.repository.updateReferenceStatus(input.reference.referenceId, 'failed');
				return {
					status: 'failed',
					diagnostics: [
						...input.diagnostics,
						createExecutionDiagnostic({
							kind: 'rebuild-failed',
							level: 'error',
							message: `替换重建失败：无法创建新的目标实体 ${input.reference.referenceId}`,
							referenceId: input.reference.referenceId,
							sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
							targetEntityId: input.reference.targetEntityId,
						}),
					],
				};
			}

			const createdItem = factoryResult.createdItems[0];
			if (!createdItem) {
				await input.repository.updateReferenceStatus(input.reference.referenceId, 'failed');
				return {
					status: 'failed',
					diagnostics: [
						...input.diagnostics,
						createExecutionDiagnostic({
							kind: 'rebuild-failed',
							level: 'error',
							message: `替换重建失败：未创建新的目标实体 ${input.reference.referenceId}`,
							referenceId: input.reference.referenceId,
							sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
							targetEntityId: input.reference.targetEntityId,
						}),
					],
				};
			}
			createdTargetEntity = createdItem.targetEntity;
			transientTargetPersistentId = createdItem.targetEntity.persistentId;
			/**
			 * replacement 会先让旧对象退出场景，因此必须在退役前冻结所属闭合集合；
			 * 后续成员迁移只消费这份正式父子关系快照，不再探测对象上的隐式能力。
			 */
			const closedCollectionBeforeReplacement = isSketchCollectionObject(
				input.targetEntity.parent,
			)
				? input.targetEntity.parent
				: null;
			input.transaction?.recordBeforeImageRestoration({
				targetSketchId: input.reference.targetSketchId,
				batchKey: input.batchKey,
				description: `恢复 replacement before-image ${input.reference.referenceId}`,
				restoreRepository: () => input.repository.saveReferences([input.reference]),
				restoreRuntime: () => {
					const rollbackDiagnostics = this.rollbackReplacementCandidate({
						reference: input.reference,
						targetSketch: input.targetSketch,
						oldTargetEntity: input.targetEntity,
						createdTargetEntity: createdItem.targetEntity,
						transientTargetPersistentId: transientTargetPersistentId!,
						replacementIdentityApplied,
						targetRetirement,
						collectionReplacementSnapshot,
						runtimeSourceName: input.runtimeSourceName,
						definitionChangeOwnerId: input.definitionChangeOwnerId,
					});
					return rollbackDiagnostics;
				},
				finalizeRuntime: () => {
					if (!targetRetirement) {
						return;
					}
					const finalizeDiagnostics = this.cleanupCommittedReplacementSource({
						reference: input.reference,
						targetEntity: input.targetEntity,
						targetRetirement,
					});
					return finalizeDiagnostics;
				},
			});
			targetRetirement = this.beginReplacementIdentityTransfer({
				targetSketch: input.targetSketch,
				oldTargetEntity: input.targetEntity,
				newTargetEntity: createdItem.targetEntity,
			});
			replacementIdentityApplied = true;

			const updatedReference = buildSketchEntityReferenceRecord({
				referenceId: input.reference.referenceId,
				projectionResult: input.nextProjectionResult,
				targetSketchId: input.reference.targetSketchId,
				targetEntityId: input.reference.targetEntityId,
				sourceDisplayLabel: input.sourceDisplayLabel,
				selectionIntent: input.reference.selectionIntent,
				boundaryMemberState: input.reference.boundaryMemberState,
			});
			const attachResult = input.targetSketch.runDefinitionChangeOwnerScope(input.definitionChangeOwnerId, () =>
				this.relationAttachDispatcher.attach({
					reference: updatedReference,
					projectionResult: input.nextProjectionResult,
					targetEntity: createdItem.targetEntity,
					runtimeSourceName: input.runtimeSourceName ?? undefined,
				})
			);
			input.diagnostics.push(...attachResult.diagnostics);
			if (attachResult.diagnostics.some(diagnostic => diagnostic.level === 'error') || !attachResult.record) {
				if (input.transaction) {
					input.diagnostics.push(...await input.transaction.rollbackBatch(input.batchKey));
				} else {
					this.rollbackReplacementCandidate({
						reference: input.reference,
						targetSketch: input.targetSketch,
						oldTargetEntity: input.targetEntity,
						createdTargetEntity: createdItem.targetEntity,
						transientTargetPersistentId,
						replacementIdentityApplied,
						targetRetirement,
						collectionReplacementSnapshot,
						runtimeSourceName: input.runtimeSourceName,
						definitionChangeOwnerId: input.definitionChangeOwnerId,
					});
				}
				await input.repository.updateReferenceStatus(input.reference.referenceId, 'failed');
				return {
					status: 'failed',
					diagnostics: [
						...input.diagnostics,
						createExecutionDiagnostic({
							kind: 'rebuild-failed',
							level: 'error',
							message: `替换重建失败：新目标实体引用约束挂载失败 ${input.reference.referenceId}`,
							referenceId: input.reference.referenceId,
							sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
							targetEntityId: createdItem.targetEntity.persistentId,
						}),
					],
				};
			}

			const savedReference: SketchEntityReferenceRecord = {
				...updatedReference,
				projectionState: attachResult.record.projectionState ?? updatedReference.projectionState,
			};
			collectionReplacementSnapshot = this.beginClosedCollectionSourceReplacement({
				collection: closedCollectionBeforeReplacement,
				oldTargetEntity: input.targetEntity,
				newTargetEntity: createdItem.targetEntity,
			});

			this.writeReferenceTag(createdItem.targetEntity, savedReference.referenceId);
			if (!input.isInputCurrent()) {
				throw STALE_REFERENCE_REBUILD;
			}
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				input.repository,
				input.isInputCurrent,
				STALE_REFERENCE_REBUILD,
				async tx => {
					if (!input.isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
					await tx.saveReferences([savedReference]);
					if (!input.isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
					await tx.updateReferenceStatus(savedReference.referenceId, 'active');
					if (!input.isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
				},
			);
			if (!input.isInputCurrent()) {
				throw STALE_REFERENCE_REBUILD;
			}
			if (!input.transaction && targetRetirement) {
				input.diagnostics.push(
					...input.targetSketch.runDefinitionChangeOwnerScope(input.definitionChangeOwnerId, () =>
						this.cleanupCommittedReplacementSource({
							reference: input.reference,
							targetEntity: input.targetEntity,
							targetRetirement,
						})
					)
				);
			}
			return {
				status: 'kept',
				diagnostics: input.diagnostics,
				runtimeItem: {
					referenceId: savedReference.referenceId,
					projectionResult: input.nextProjectionResult,
					targetEntity: createdItem.targetEntity,
					targetChange: {
						referenceId: savedReference.referenceId,
						targetSketchId: savedReference.targetSketchId,
						oldTargetEntityId: input.reference.targetEntityId,
						newTargetEntityId: savedReference.targetEntityId,
						changeKind: 'replaced',
					},
					targetSketchBaselineDefinitionRevision: input.targetSketchBaselineDefinitionRevision,
				},
			};
		} catch (error) {
			if (error === STALE_REFERENCE_REBUILD || !input.isInputCurrent()) {
				if (input.transaction) {
					throw STALE_REFERENCE_REBUILD;
				}
				if (createdTargetEntity && transientTargetPersistentId) {
					this.rollbackReplacementCandidate({
						reference: input.reference,
						targetSketch: input.targetSketch,
						oldTargetEntity: input.targetEntity,
						createdTargetEntity,
						transientTargetPersistentId,
						replacementIdentityApplied,
						targetRetirement,
						collectionReplacementSnapshot,
						runtimeSourceName: input.runtimeSourceName,
						definitionChangeOwnerId: input.definitionChangeOwnerId,
					});
				}
				return {status: 'kept', diagnostics: []};
			}
			const cleanupDiagnostics: ExecutionDiagnostic[] = [];
			if (input.transaction) {
				cleanupDiagnostics.push(...await input.transaction.rollbackBatch(input.batchKey));
			} else if (createdTargetEntity && transientTargetPersistentId) {
				cleanupDiagnostics.push(...this.rollbackReplacementCandidate({
					reference: input.reference,
					targetSketch: input.targetSketch,
					oldTargetEntity: input.targetEntity,
					createdTargetEntity,
					transientTargetPersistentId,
					replacementIdentityApplied,
					targetRetirement,
					collectionReplacementSnapshot,
					runtimeSourceName: input.runtimeSourceName,
					definitionChangeOwnerId: input.definitionChangeOwnerId,
				}));
			}
			await input.repository.updateReferenceStatus(input.reference.referenceId, 'failed');
			return {
				status: 'failed',
				diagnostics: [
					...input.diagnostics,
					...cleanupDiagnostics,
					createExecutionDiagnostic({
						kind: 'rebuild-failed',
						level: 'error',
						message: `替换重建引用失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: input.reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
						targetEntityId: input.reference.targetEntityId,
					}),
				],
			};
		}
	}

	/**
	 * 回滚原位重建。
	 * 说明：
	 * 1. same-kind 路径会先拆旧 relation、再写新几何；
	 * 2. 若后续 attach / 保存记录失败，必须把目标几何恢复到重建前快照；
	 * 3. 失败态下不恢复旧 relation，只保留“目标几何恢复 + 当前 relation 清空”的最小一致状态，
	 *    对齐参考分支的失败收口策略。
	 *
	 * @param input 回滚输入。
	 */
	private rollbackInPlaceRebuild(input: {
		reference: Pick<SketchEntityReferenceRecord, 'referenceId' | 'projectionResultKind'>;
		targetEntity: Object3D;
		targetSnapshot: SketchEntityReferenceTargetSnapshot;
	}): void {
		this.relationAttachDispatcher.detach({
			reference: input.reference,
			targetEntity: input.targetEntity,
		});
		this.targetSnapshotService.restore(input.targetEntity, input.targetSnapshot);
	}

	/**
	 * 回滚已经过期的原位重建，并恢复事务开始前的运行时关系。
	 *
	 * 普通重建失败会按既有策略留下 failed 且无 relation 的状态；文档换代导致的 stale 则不属于业务失败，
	 * 不能让旧文档的一次异步重建产生可观察副作用。因此这里除恢复目标几何外，还必须依据持久化旧记录
	 * 重挂事务开始前的 relation，确保新文档接管前旧运行时仍保持完整。
	 *
	 * @param input 过期原位重建的补偿输入。
	 */
	private rollbackStaleInPlaceRebuild(input: {
		reference: SketchEntityReferenceRecord;
		targetSketch: SketchGroup;
		targetEntity: Object3D;
		targetSnapshot: SketchEntityReferenceTargetSnapshot;
		runtimeSourceName: string | null;
		definitionChangeOwnerId: string;
	}): void {
		input.targetSketch.runDefinitionChangeOwnerScope(input.definitionChangeOwnerId, () => {
			this.rollbackInPlaceRebuild({
				reference: input.reference,
				targetEntity: input.targetEntity,
				targetSnapshot: input.targetSnapshot,
			});

			const restoredAttachResult = this.relationAttachDispatcher.attach({
				reference: input.reference,
				projectionResult: restoreProjectionResultFromReferenceRecord(input.reference),
				targetEntity: input.targetEntity,
				runtimeSourceName: input.runtimeSourceName ?? undefined,
			});
			if (
				restoredAttachResult.diagnostics.some(diagnostic => diagnostic.level === 'error') ||
				!restoredAttachResult.record
			) {
				throw new Error(`过期原位重建回滚后无法恢复旧引用关系: ${input.reference.referenceId}`);
			}
			this.writeReferenceTag(input.targetEntity, input.reference.referenceId);
		});
	}

	/**
	 * 让 replacement 新实例接管旧实体的持久身份和场景顺序。
	 *
	 * @param input 新旧目标及所属草图。
	 * @returns 延迟到 repository 提交后完成的旧目标退役句柄。
	 */
	private beginReplacementIdentityTransfer(input: {
		targetSketch: SketchGroup;
		oldTargetEntity: Object3D & ISketchObject;
		newTargetEntity: Object3D & ISketchObject;
	}): SketchEntityReferenceTargetRetirement {
		const oldParent = input.oldTargetEntity.parent;
		if (!oldParent) {
			throw new Error(`replacement 旧目标不在正式草图中: ${input.newTargetEntity.persistentId}`);
		}
		const oldChildIndex = oldParent.children.indexOf(input.oldTargetEntity);
		return input.targetSketch.runEntityReplacementScope(() => {
			input.newTargetEntity.parent?.remove(input.newTargetEntity);
			const targetRetirement = this.objectCleanupService.beginTargetRetirement(
				input.oldTargetEntity,
			);
			input.newTargetEntity.persistentId = input.oldTargetEntity.persistentId;
			addObject3DToParent(
				input.newTargetEntity,
				oldParent,
				oldChildIndex >= 0 ? oldChildIndex : undefined,
			);
			return targetRetirement;
		});
	}

	/**
	 * 回滚 replacement 的身份交换、闭合集合成员迁移和新实例资源。
	 *
	 * @param input replacement before-image。
	 * @returns 回滚阶段诊断。
	 */
	private rollbackReplacementCandidate(input: {
		reference: SketchEntityReferenceRecord;
		targetSketch: SketchGroup;
		oldTargetEntity: Object3D;
		createdTargetEntity: Object3D & ISketchObject;
		transientTargetPersistentId: string;
		replacementIdentityApplied: boolean;
		targetRetirement: SketchEntityReferenceTargetRetirement | null;
		collectionReplacementSnapshot: SketchEntityReferenceCollectionReplacementSnapshot | null;
		runtimeSourceName: string | null;
		definitionChangeOwnerId: string;
	}): ExecutionDiagnostic[] {
		const diagnostics: ExecutionDiagnostic[] = [];
		input.targetSketch.runDefinitionChangeOwnerScope(input.definitionChangeOwnerId, () => {
			input.targetSketch.runEntityReplacementScope(() => {
				if (input.collectionReplacementSnapshot) {
					this.rollbackClosedCollectionSourceReplacement(
						input.collectionReplacementSnapshot,
					);
				}
				if (input.replacementIdentityApplied) {
					input.createdTargetEntity.parent?.remove(input.createdTargetEntity);
					input.createdTargetEntity.persistentId = input.transientTargetPersistentId;
					input.targetRetirement?.rollback();
				}
				diagnostics.push(...this.cleanupTransientReplacementTarget({
					referenceId: input.reference.referenceId,
					projectionResultKind: input.reference.projectionResultKind,
					targetEntity: input.createdTargetEntity,
				}));
			});

			const restoredAttachResult = this.relationAttachDispatcher.attach({
				reference: input.reference,
				projectionResult: restoreProjectionResultFromReferenceRecord(input.reference),
				targetEntity: input.oldTargetEntity,
				runtimeSourceName: input.runtimeSourceName ?? undefined,
			});
			diagnostics.push(...restoredAttachResult.diagnostics);
			if (
				!restoredAttachResult.record
				|| restoredAttachResult.diagnostics.some(item => item.level === 'error')
			) {
				diagnostics.push(createExecutionDiagnostic({
					kind: 'compensation-failed',
					level: 'error',
					message: `replacement 回滚后无法恢复旧引用关系: ${input.reference.referenceId}`,
					referenceId: input.reference.referenceId,
					targetEntityId: input.reference.targetEntityId,
				}));
			}
			this.writeReferenceTag(input.oldTargetEntity, input.reference.referenceId);
		});
		return diagnostics;
	}

	/**
	 * 清理 kind-changed 路径里尚未正式切换成功的新目标实体。
	 * 说明：
	 * 1. 替换重建在保存新引用记录成功前，不得影响旧 target / 旧 relation；
	 * 2. 因此一旦新 target 创建后任一步失败，必须撤掉新 relation、清 tag、再回收对象；
	 * 3. 这里只处理“暂存新目标”的回滚，不触碰旧目标。
	 *
	 * @param input 清理输入。
	 */
	private cleanupTransientReplacementTarget(input: {
		referenceId: string;
		projectionResultKind: ProjectionResult['projectionResultKind'];
		targetEntity: Object3D & ISketchObject;
	}): ExecutionDiagnostic[] {
		const diagnostics: ExecutionDiagnostic[] = [];

		try {
			this.relationAttachDispatcher.detach({
				reference: {
					referenceId: input.referenceId,
					projectionResultKind: input.projectionResultKind,
				},
				targetEntity: input.targetEntity,
			});
		} catch (error) {
			diagnostics.push(
				createExecutionDiagnostic({
					kind: 'restore-failed',
					level: 'warning',
					message: `替换重建回滚时清理暂存 relation 失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: input.referenceId,
					targetEntityId: input.targetEntity.persistentId,
				})
			);
		}

		try {
			this.clearReferenceTag(input.targetEntity);
		} catch (error) {
			diagnostics.push(
				createExecutionDiagnostic({
					kind: 'tag-cleanup-failed',
					level: 'warning',
					message: `替换重建回滚时清理暂存目标引用标记失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: input.referenceId,
					targetEntityId: input.targetEntity.persistentId,
				})
			);
		}

		try {
			this.objectCleanupService.cleanupTargetEntity(input.targetEntity);
		} catch (error) {
			diagnostics.push(
				createExecutionDiagnostic({
					kind: 'target-dispose-failed',
					level: 'warning',
					message: `替换重建回滚时释放暂存目标实体失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: input.referenceId,
					targetEntityId: input.targetEntity.persistentId,
				})
			);
		}

		return diagnostics;
	}

	/**
	 * 清理已悬空的引用记录。
	 * 说明：
	 * 1. 复用参考分支的思路：source / target 缺失属于“记录已失效”，优先移除记录而不是保留 failed 垃圾状态；
	 * 2. 若目标实体仍在草图中，只解除 relation 与引用 tag，不删除几何本身；
	 * 3. 一旦清理记录本身失败，再退回 failed 状态，避免静默丢失上下文。
	 *
	 * @param input 清理输入。
	 * @returns 清理结果。
	 */
	private async cleanupDanglingReference(input: {
		reference: SketchEntityReferenceRecord;
		repository: SketchEntityReferenceRepositoryWriteScope;
		targetEntity?: Object3D | null;
		cleanupTargetEntity?: boolean;
		diagnostic: ExecutionDiagnostic;
		isInputCurrent?: () => boolean;
	}): Promise<{
		status: 'kept' | 'removed' | 'failed';
		diagnostics: ExecutionDiagnostic[];
	}> {
		const isInputCurrent = input.isInputCurrent ?? (() => true);
		if (!isInputCurrent()) {
			return {status: 'kept', diagnostics: []};
		}
		try {
			await executeSketchEntityReferenceRepositoryWrite(
				this.dependencies.repository,
				input.repository,
				isInputCurrent,
				STALE_REFERENCE_REBUILD,
				async tx => {
					if (!isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
					await tx.removeReferences([input.reference.referenceId]);
					if (!isInputCurrent()) {
						throw STALE_REFERENCE_REBUILD;
					}
				},
			);
		} catch (error) {
			if (error === STALE_REFERENCE_REBUILD || !isInputCurrent()) {
				return {status: 'kept', diagnostics: []};
			}
			await input.repository.updateReferenceStatus(input.reference.referenceId, 'failed');
			return {
				status: 'failed',
				diagnostics: [
					input.diagnostic,
					createExecutionDiagnostic({
						kind: 'rebuild-failed',
						level: 'error',
						message: `清理失效引用失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: input.reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
						targetEntityId: input.reference.targetEntityId,
					}),
				],
			};
		}
		if (!isInputCurrent()) {
			return {status: 'kept', diagnostics: []};
		}

		const diagnostics: ExecutionDiagnostic[] = [input.diagnostic];

		try {
			this.relationAttachDispatcher.detach({
				reference: input.reference,
				targetEntity: input.targetEntity,
			});
		} catch (error) {
			diagnostics.push(
				createExecutionDiagnostic({
					kind: 'restore-failed',
					level: 'warning',
					message: `移除失效引用后清理旧 relation 失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: input.reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
					targetEntityId: input.reference.targetEntityId,
				})
			);
		}

		if (input.targetEntity) {
			try {
				this.clearReferenceTag(input.targetEntity);
			} catch (error) {
				diagnostics.push(
					createExecutionDiagnostic({
						kind: 'tag-cleanup-failed',
						level: 'warning',
						message: `移除失效引用后清理目标引用标记失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: input.reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
						targetEntityId: input.reference.targetEntityId,
					})
				);
			}
		}

		if (input.cleanupTargetEntity === true && input.targetEntity) {
			try {
				this.objectCleanupService.cleanupTargetEntity(input.targetEntity);
			} catch (error) {
				diagnostics.push(
					createExecutionDiagnostic({
						kind: 'target-dispose-failed',
						level: 'warning',
						message: `移除失效引用后释放目标实体失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: input.reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
						targetEntityId: input.reference.targetEntityId,
					})
				);
			}
		}

		return {
			status: 'removed',
			diagnostics,
		};
	}

	/**
	 * 在 kind-changed 新记录提交成功后，清理被替换掉的旧目标实体。
	 * 说明：
	 * 1. 此时仓储已经以“新 target + 新记录”为真相源，不能再因为旧对象清理失败把整条引用打回 failed；
	 * 2. 因此旧 relation / 旧 tag / 旧对象的处理统一采用 best-effort，并将异常转换为 warning 诊断；
	 * 3. 这样可以贴近参考分支“提交成功后仅做收尾清理”的顺序，避免仓储与场景状态出现混合回滚。
	 *
	 * @param input 清理输入。
	 * @returns 收尾清理诊断。
	 */
	private cleanupCommittedReplacementSource(input: {
		reference: Pick<SketchEntityReferenceRecord, 'referenceId' | 'projectionResultKind' | 'sourceDescriptor' | 'targetEntityId'>;
		targetEntity: Object3D & ISketchObject;
		targetRetirement: SketchEntityReferenceTargetRetirement;
	}): ExecutionDiagnostic[] {
		const diagnostics: ExecutionDiagnostic[] = [];

		/**
		 * 新 relation 挂载时已经按 referenceId 拆除旧绑定并登记新绑定。这里若再次调用
		 * dispatcher.detach，会命中新目标的当前绑定并误删刚挂好的 relation。旧目标已在
		 * replacement 事务中退出闭合集合，收尾阶段只清理旧 tag 与自有资源。
		 */

		try {
			this.clearReferenceTag(input.targetEntity);
		} catch (error) {
			diagnostics.push(
				createExecutionDiagnostic({
					kind: 'tag-cleanup-failed',
					level: 'warning',
					message: `替换重建后清理旧目标引用标记失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: input.reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
					targetEntityId: input.reference.targetEntityId,
				})
			);
		}

		try {
			input.targetRetirement.commit();
		} catch (error) {
			diagnostics.push(
				createExecutionDiagnostic({
					kind: 'target-dispose-failed',
					level: 'warning',
					message: `替换重建后释放旧目标实体失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: input.reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
					targetEntityId: input.reference.targetEntityId,
				})
			);
		}

		return diagnostics;
	}

	/**
	 * 在 replacement 提交前把旧边在闭合集合中的成员位置迁移给新边。
	 *
	 * @param input replacement 新旧目标。
	 * @returns 存在闭合集合成员迁移时返回可回滚快照。
	 */
	private beginClosedCollectionSourceReplacement(input: {
		collection: SketchCollectionObject | null;
		oldTargetEntity: Object3D & ISketchObject;
		newTargetEntity: Object3D & ISketchObject;
	}): SketchEntityReferenceCollectionReplacementSnapshot | null {
		const closedCollection = input.collection;
		if (!closedCollection) {
			return null;
		}
		const previousSource = this.resolveCollectionSource(input.oldTargetEntity);
		if (!previousSource) {
			throw new Error(`replacement 旧目标不支持闭合 Wire 成员语义: ${input.oldTargetEntity.persistentId}`);
		}
		const replacementSource = this.resolveCollectionSource(input.newTargetEntity);
		if (!replacementSource) {
			throw new Error(`replacement 新目标不支持闭合 Wire 成员语义: ${input.newTargetEntity.persistentId}`);
		}
		const sourcesBefore = [...closedCollection.collectSet];
		const sourceIndex = sourcesBefore.findIndex(source => source === previousSource);
		if (sourceIndex < 0) {
			throw new Error(`replacement 旧目标未登记在所属闭合 Wire 中: ${input.oldTargetEntity.uuid}`);
		}
		const sourcesAfter = [...sourcesBefore];
		sourcesAfter[sourceIndex] = replacementSource;
		closedCollection.setSources(sourcesAfter);
		return {collection: closedCollection, sourcesBefore};
	}

	/**
	 * 回滚 replacement 对闭合集合成员的迁移。
	 *
	 * @param snapshot 迁移前快照。
	 */
	private rollbackClosedCollectionSourceReplacement(
		snapshot: SketchEntityReferenceCollectionReplacementSnapshot,
	): void {
		snapshot.collection.setSources([...snapshot.sourcesBefore]);
	}

	/**
	 * 把 replacement 新目标收窄为闭合集合支持的曲线来源。
	 *
	 * @param targetEntity replacement 新目标。
	 * @returns 可进入 SketchCollectionObject 的来源；不支持时返回 null。
	 */
	private resolveCollectionSource(
		targetEntity: Object3D & ISketchObject,
	): CollectSource | null {
		return isSketchLineObject(targetEntity)
			|| isSketchArcObject(targetEntity)
			|| isSketchParabolaObject(targetEntity)
			|| isSketchSplineObject(targetEntity)
			|| isSketchStyleSplineObject(targetEntity)
			? targetEntity
			: null;
	}

	/**
	 * 写入引用标记。
	 *
	 * @param targetObject 目标对象。
	 * @param referenceId 引用记录 id。
	 */
	private writeReferenceTag(targetObject: Object3D, referenceId: string): void {
		if (this.dependencies.runtime.writeReferenceTag) {
			this.dependencies.runtime.writeReferenceTag(targetObject, referenceId);
			return;
		}

		writeSketchEntityReferenceTag(targetObject, {
			referenceId,
			isReferencedEntity: true,
		});
	}

	/**
	 * 清理引用标记。
	 *
	 * @param targetObject 目标对象。
	 */
	private clearReferenceTag(targetObject: Object3D): void {
		if (this.dependencies.runtime.clearReferenceTag) {
			this.dependencies.runtime.clearReferenceTag(targetObject);
			return;
		}

		clearSketchEntityReferenceTag(targetObject);
	}
}
